/* eslint-disable no-underscore-dangle */
import {useState, useEffect} from 'react';
import PropTypes from 'prop-types';
import {useDebtorQuery} from 'Shared/util/web-api-hooks';
import {formatDate, formatShortDate, formatAmount} from 'Shared/util/formatters';
import DataTable, {ColumnTypes} from 'Shared/components/tables/data-table';
import Page from 'Shared/components/page/page';
import {getPieChartFilterValue} from 'Shared/components/charts/pie-chart-utils';
import {useTranslation} from 'Shared/util/custom-hooks';
import {translateColumns, funcFilterStatus, funcFilterProperties} from 'Shared/util/translations';
import useTranslationContext from 'Contexts/translation-context';
import DebtorsFilter from './debtors-filter';
import {getBarChartData} from './debtor-utils';
import DebtorPieChart from './debtor-pie-chart';
import DebtorBarChart from './debtor-bar-chart';
import styles from './debtor-details-page.css';


const modifyStatus = (status) => (status === 'notDue' ? '' : status);

const columns = [{
  title: 'invoiceNumber',
  key: 'number',
}, {
  title: 'invoiceDate',
  key: 'invoiceDate',
  type: ColumnTypes.Date,
  formatter: (d) => formatDate(d),
}, {
  title: 'dueDate',
  key: 'dueDate',
  type: ColumnTypes.Date,
  formatter: (d) => formatDate(d),
  hiddenMobile: true,
}, {
  title: 'service',
  key: 'service',
  hiddenMobile: true,
}, {
  title: 'remainingAmount',
  key: 'remainingAmount',
  type: ColumnTypes.Amount,
  formatter: (d) => formatAmount(d),
  footer: {
    sum: true,
  },
}, {
  title: 'currency',
  key: 'currencyCode',
  footer: {
    groupBy: true,
  },
}, {
  title: 'status',
  key: 'status',
  formatter: (d) => modifyStatus(d),
}];

const getRowLink = (row) => `/invoices/${row.id}`;


const DebtorDetailsPage = ({
  match,
}) => {
  const [debtor, setDebtor] = useState({});
  const [currentLedger, setCurrentLedger] = useState([]);
  const [averagePayments, setAveragePayments] = useState([]);
  const {translationData} = useTranslationContext();
  const t = useTranslation(x => x.debtorsPage);
  const tc = useTranslation(x => x.invoiceStatus);
  const tg = useTranslation(x => x.global);
  const ts = useTranslation(x => x.servicesMapOnId);
  const [debtorResult, loading] = useDebtorQuery(match.params.debtorId);

  useEffect(() => {
    if (debtorResult && !loading) {
      setDebtor(debtorResult.debtor);
      setCurrentLedger(debtorResult.currentLedger);
      setAveragePayments(debtorResult.averagePayments);
    }
  }, [debtorResult, loading]);

  const barChartData = formatShortDate(getBarChartData(averagePayments), translationData.languageCode);

  return (
    <Page
      title={debtor.name || ''}
      loading={loading}
    >
      <DebtorsFilter
        default={{
          pieChartLabelWithIndex: null,
        }}
        items={funcFilterStatus(currentLedger, tc, 'status')}
        render={(filterArgs) => {
          const selectedPieChartIndex = filterArgs.filter.pieChartLabelWithIndex && parseInt(filterArgs.filter.pieChartLabelWithIndex.split('::')[1], 10);

          const handlePieChartFilter = (label, index) => {
            const newPieChartFilterValue = getPieChartFilterValue(selectedPieChartIndex, index, label);
            filterArgs.handleChange({pieChartLabelWithIndex: newPieChartFilterValue});
          };

          return (
            <>
              <div className={styles.chartContainer}>
                <DebtorPieChart
                  headerText={t.currentLedger}
                  invoices={currentLedger}
                  onClick={(element, chart) => handlePieChartFilter(chart.props.data.labels[element._index], element._index)}
                  legend={{
                    onClick: (label, index) => handlePieChartFilter(label, index),
                    selectedIndex: selectedPieChartIndex,
                  }}
                />
                <DebtorBarChart
                  headerText={t.averagePaymentPeriodAfterDueDate}
                  label={t.paymentTime}
                  data={barChartData}
                  debtor={debtor.name}
                />
              </div>

              <DataTable
                columns={translateColumns(columns, tg)}
                rows={funcFilterProperties(filterArgs.items, ts, ['serviceIdTranslationTag'], ['service'])}
                getRowLink={getRowLink}
              />
            </>
          );
        }}
      />
    </Page>
  );
};

DebtorDetailsPage.propTypes = {
  match: PropTypes.shape({
    params: PropTypes.shape({debtorId: PropTypes.string.isRequired}).isRequired,
  }).isRequired,
};

export default DebtorDetailsPage;
